// @ts-strict-ignore
import React from 'react';
import { Text, Counter } from '@usealloy/component-library';
import moment from 'moment';
import { formatCurrencyIntl } from '~/helpers/i18n';
import { TransactionStatusBadge } from '../TransactionStatusBadge';
import type { WebhookAuditEventDetails } from '~/investigations/redux/types';

const UNKNOWN_VALUE = '—';

export const getFilterOptions = (column, data) => {
  const keyedOptions = { All: { label: 'All', value: 'All' } };
  data.forEach(datum => {
    const columnValue = datum[column];
    const hasValue = columnValue !== null && columnValue !== undefined;
    if (!keyedOptions[columnValue] && hasValue) {
      keyedOptions[columnValue] = { label: columnValue, value: columnValue };
    }
  });
  return Object.values(keyedOptions);
};

export const getTransactionColumns = (
  webhookAuditEventDetailsByTransactionId?: { [transactionId: string]: WebhookAuditEventDetails },
  includeEvidenceColumn = false,
  onEvidenceClick?: (transaction: any) => void,
) => ([
  {
    header: 'Transaction ID',
    id: 'external_transaction_identifier',
    enableSorting: true,
    accessorFn: ({ external_transaction_identifier }) => (
      external_transaction_identifier || UNKNOWN_VALUE
    ),
  },
  {
    header: 'Date',
    id: 'created_at',
    enableSorting: true,
    accessorFn: ({ created_at }) => (created_at
      ? moment(created_at).format('MM/DD/YYYY')
      : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Account Number',
    id: 'source_bank_account_number',
    enableSorting: true,
    accessorFn: ({ source_bank_account_number }) => source_bank_account_number || UNKNOWN_VALUE,
  },
  {
    header: 'Product Name',
    id: 'source_bank_product_name',
    enableSorting: true,
    accessorFn: ({ source_bank_product_name }) => source_bank_product_name || UNKNOWN_VALUE,
  },
  {
    header: 'Amount',
    id: 'amount',
    enableSorting: true,
    accessorFn: ({ amount }) => amount,
    cell: ({ row: { original: { amount, currency_code: alphabeticCurrencyCode } } }) => (
      <Text style={{ color: amount < 0 ? 'var(--cl-red-500)' : '' }}>{formatCurrencyIntl(amount, { alphabeticCurrencyCode }).formattedNumber}</Text>
    ),
  },
  {
    header: 'Status',
    id: 'status',
    enableSorting: true,
    accessorFn: ({ status }) => status || UNKNOWN_VALUE,
    cell: ({ row: { original } }) => (
      original?.status
        ? (
          <TransactionStatusBadge
            status={original.status}
            webhookAuditEventDetailsByTransactionId={webhookAuditEventDetailsByTransactionId}
            externalTransactionId={original.external_transaction_identifier}
          />
        ) : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Transaction Type',
    id: 'type_description',
    enableSorting: true,
    accessorFn: ({ type_description }) => type_description || UNKNOWN_VALUE,
  },
  {
    header: 'Transaction Description',
    id: 'description',
    enableSorting: true,
    accessorFn: ({ description }) => description || UNKNOWN_VALUE,
  },
  {
    header: 'MCC',
    id: 'target_bank_merchant_category_code',
    enableSorting: true,
    accessorFn: ({ target_bank_merchant_category_code }) => (
      target_bank_merchant_category_code || UNKNOWN_VALUE
    ),
  },
  {
    header: 'MCC Description',
    id: 'target_bank_mcc_description',
    enableSorting: true,
    accessorFn: ({ target_bank_mcc_description }) => target_bank_mcc_description || UNKNOWN_VALUE,
  },
  {
    header: 'Evidence',
    id: 'evidence_count',
    enableSorting: true,
    accessorFn: ({ evidence_count }) => evidence_count || 0,
    cell: ({ row: { original } }) => {
      if (!original.evidence_count || original.evidence_count === 0) return '';

      return (
        <button
          type="button"
          onClick={() => onEvidenceClick?.(original)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
          aria-label={`View ${original.evidence_count} evidence item${
            original.evidence_count > 1 ? 's' : ''
          }`}
        >
          <Counter count={original.evidence_count} />
        </button>
      );
    },
  },
  {
    header: 'Currency Code',
    id: 'currency_code',
    enableSorting: true,
    accessorFn: ({ currency_code }) => currency_code || UNKNOWN_VALUE,
  },
  {
    header: 'Transaction Code',
    id: 'type_code',
    enableSorting: true,
    accessorFn: ({ type_code }) => type_code || UNKNOWN_VALUE,
  },
  {
    header: 'Process Method',
    id: 'process_method',
    enableSorting: true,
    accessorFn: ({ process_method }) => process_method || UNKNOWN_VALUE,
  },
  {
    header: 'Category',
    id: 'category',
    enableSorting: true,
    accessorFn: ({ category }) => category || UNKNOWN_VALUE,
  },
  {
    header: 'Counter-Party Country Code',
    id: 'target_bank_country_code',
    enableSorting: true,
    accessorFn: ({ target_bank_country_code }) => target_bank_country_code || UNKNOWN_VALUE,
  },
  {
    header: 'Counter-Party Name',
    id: 'target_bank_account_name',
    enableSorting: true,
    accessorFn: ({ target_bank_account_name }) => target_bank_account_name || UNKNOWN_VALUE,
  },
  {
    header: 'Counter-Party Sanctions',
    id: 'matches',
    enableSorting: true,
    accessorFn: ({ matches }) => (matches >= 0 ? matches : UNKNOWN_VALUE),
  },
  {
    header: 'Settled At',
    id: 'settled_at',
    enableSorting: true,
    accessorFn: ({ settled_at }) => settled_at || UNKNOWN_VALUE,
  },
  {
    header: 'Is Approved',
    id: 'is_approved',
    enableSorting: true,
    accessorFn: ({ is_approved }) => (
      typeof is_approved === 'boolean' ? is_approved : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Interaction Point',
    id: 'interaction_point',
    enableSorting: true,
    accessorFn: ({ interaction_point }) => interaction_point || UNKNOWN_VALUE,
  },
  {
    header: 'Is Reversal',
    id: 'is_reversal',
    enableSorting: true,
    accessorFn: ({ is_reversal }) => (
      typeof is_reversal === 'boolean' ? is_reversal : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Card Process Mode',
    id: 'card_process_mode',
    enableSorting: true,
    accessorFn: ({ card_process_mode }) => card_process_mode || UNKNOWN_VALUE,
  },
  {
    header: 'Is International Override',
    id: 'is_international_override',
    enableSorting: true,
    accessorFn: ({ is_international_override }) => (
      typeof is_international_override === 'boolean' ? is_international_override : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Device Type Code',
    id: 'device_type_code',
    enableSorting: true,
    accessorFn: ({ device_type_code }) => device_type_code || UNKNOWN_VALUE,
  },
  {
    header: 'Approval Code',
    id: 'approval_code',
    enableSorting: true,
    accessorFn: ({ approval_code }) => approval_code || UNKNOWN_VALUE,
  },
  {
    header: 'Batch Identifier',
    id: 'batch_identifier',
    enableSorting: true,
    accessorFn: ({ batch_identifier }) => batch_identifier || UNKNOWN_VALUE,
  },
  {
    header: 'Is Triggered By User',
    id: 'is_triggered_by_user',
    enableSorting: true,
    accessorFn: ({ is_triggered_by_user }) => (
      typeof is_triggered_by_user === 'boolean' ? is_triggered_by_user : UNKNOWN_VALUE
    ),
  },
  {
    header: 'Outcome',
    id: 'outcome',
    enableSorting: true,
    accessorFn: ({ outcome }) => outcome || UNKNOWN_VALUE,
  },
].filter(col => includeEvidenceColumn || col.id !== 'evidence_count'));
